import type { Curve } from '../../../../interfaces/toolData';

/**
 * Specialized parser for telemetry files that use variable/value pairs
 * These files contain telemetry variable names and their corresponding values with timestamp inheritance
 */
export class TelemetryParser {
  /**
   * Create curves from telemetry key-value data format
   * @param rawData Parsed CSV rows as key-value objects
   * @param timeValues Array of timestamps for timestamp inheritance
   * @param headers CSV header columns
   * @returns Array of curves, one per unique telemetry variable
   */
  static createCurves(
    rawData: Record<string, string>[],
    timeValues: Date[],
    headers: string[]
  ): Curve[] {
    // Find the telemetry variable column (handle both old and new formats)
    const telemVariableCol = headers.find(h => 
      h === 'TELEM_VARIABLE' || h === 'Telem Variable'
    );
    
    // Find the value columns (prefer telemetered value over internal value)
    const telemeteredValueCol = headers.find(h => 
      h === 'TELEMETERED_VALUE' || h === 'Telemetered Value'
    );
    const valueCol = headers.find(h => 
      h === 'VALUE' || h === 'Value'
    );
    
    if (!telemVariableCol) {
      throw new Error('No telemetry variable column found in telemetry file');
    }
    
    const valueColumnToUse = telemeteredValueCol || valueCol;
    if (!valueColumnToUse) {
      throw new Error('No value column found in telemetry file');
    }
    
    // Group data by telemetry variable, filtering out String Msg entries
    const telemetryGroups = new Map<string, Array<{
      timestamp: Date;
      value: number;
      originalIndex: number;
    }>>();
    
    let lastValidTimestamp: Date | null = null;
    
    rawData.forEach((row, index) => {
      const variableName = row[telemVariableCol];
      const valueString = row[valueColumnToUse];
      
      // Skip String Msg entries and empty variable names
      if (!variableName || variableName.toLowerCase().includes('string msg')) {
        return;
      }
      
      // Handle timestamp inheritance
      let timestamp = timeValues[index];
      
      if (!timestamp || isNaN(timestamp.getTime())) {
        if (lastValidTimestamp) {
          timestamp = lastValidTimestamp;
        } else {
          return; // Skip if no valid timestamp available
        }
      } else {
        lastValidTimestamp = timestamp;
      }
      
      // Parse the value (skip if not numeric)
      const numericValue = parseFloat(valueString);
      if (isNaN(numericValue)) {
        return;
      }
      
      // Add to the appropriate group
      if (!telemetryGroups.has(variableName)) {
        telemetryGroups.set(variableName, []);
      }
      
      telemetryGroups.get(variableName)!.push({
        timestamp,
        value: numericValue,
        originalIndex: index
      });
    });
    
    // Create curves from the grouped data
    const curves: Curve[] = [];
    
    telemetryGroups.forEach((dataPoints, variableName) => {
      if (dataPoints.length === 0) return;
      
      // Extract timestamps and values for this variable
      const xValues = dataPoints.map(dp => dp.timestamp);
      const yValues = dataPoints.map(dp => dp.value);
      
      // For telemetry files, skip the validIndices filtering since inheritance 
      // creates valid timestamps that weren't in the original validIndices
      const filteredXValues = xValues;
      const filteredYValues = yValues;
      
      // Get unit for this telemetry variable
      const unit = TelemetryParser.getVariableUnit(variableName);
      
      curves.push({
        name: variableName,
        xValues: filteredXValues,
        yValues: filteredYValues,
        yUnit: unit
      });
    });
    
    return curves;
  }

  /**
   * Comprehensive unit lookup table for MicroPulse telemetry variables
   * @param variableName The telemetry variable name
   * @returns The unit string for the variable
   * @throws Error if variable is not found in lookup table
   */
  static getVariableUnit(variableName: string): string {
    const unitMap: Record<string, string> = {
      // Angular measurements
      'INCLINATION': '°',
      'Inclination': '°',
      'AZIMUTH': '°', 
      'Azimuth': '°',
      'TOOLFACE': '°',
      'Toolface': '°',
      'DIPANGLE': '°',
      'Dip Angle': '°',
      'Delta Inclination': '°',
      'Delta Azimuth': '°',
      'DELTA_INCLINATION': '°',
      'DELTA_AZIMUTH': '°',
      
      // Temperature measurements
      'TEMPERATURE': '°C',
      'Tool Temperature': '°C',
      
      // Magnetic field measurements
      'MAGF': 'G',
      'Magnetic Field': 'G',
      
      // Gravitational measurements
      'GRAV': 'g',
      'Gravity': 'g',
      
      // Accelerometer components (new 3.4.6 telemetry variables)
      'Ax': 'g',
      'Ay': 'g', 
      'Az': 'g',
      'Ax2': 'g',
      'Ay2': 'g',
      'Az2': 'g',
      
      // Calibrated accelerometer components
      'CALIBRATED_AX': 'g',
      'CALIBRATED_AY': 'g',
      'CALIBRATED_AZ': 'g',
      
      // Magnetometer components (new 3.4.6 telemetry variables)
      'Mx': 'G',
      'My': 'G',
      'Mz': 'G',
      
      // Calibrated magnetometer components
      'CALIBRATED_MX': 'G',
      'CALIBRATED_MY': 'G',
      'CALIBRATED_MZ': 'G',
      
      // Calibrated gyroscope components  
      'CALIBRATED_GX': 'dps',  // degrees per second
      'CALIBRATED_GY': 'dps',
      'CALIBRATED_GZ': 'dps',
      
      // Battery voltage telemetry
      'Battery Voltage': 'V',
      
      // Multi-sector gamma measurements
      '2-Sector Gamma Up': 'cps',
      'GAMMA_CORRECTED': 'cps',
      'GV7': 'cps',  // Gamma V7 sector
      
      // Downlink telemetry variables
      'DLNK': '',  // Downlink (dimensionless)
      
      // Directional measurements (note: Inclination and Azimuth already exist above)
      'INCL': 'degrees',  // Inclination abbreviation
      'AZIM': 'degrees',  // Azimuth abbreviation
      'AZI_TRGT': 'degrees',  // Azimuth Target
      'IH_TRGT': 'degrees',  // Inclination Target
      
      // Additional gamma measurements
      '2-Sector Gamma Down': 'cps',
      
      // Vibration and shock measurements
      'Axial Shock': 'g',
      'Axial Vibration': 'g',
      'Lateral Shock': 'g', 
      'Lateral Vibration': 'g',
      
      // RPM measurements
      'RPM': 'RPM',    // General RPM
      'CCRPM': 'RPM',  // Corrected RPM
      'LTRPM': 'RPM',  // Lateral RPM
      'UTRPM': 'RPM',  // Upper RPM
      
      // Status and control variables
      'RTSTAT': '',    // Real-time status (dimensionless)
      'RTSTAT2': '',   // Real-time status 2 (dimensionless)
      'RTSTAT4': '',   // Real-time status 4 (dimensionless)
      'POSSUM': '',    // Position summary (dimensionless)
      'PRDS': '',      // Pressure status (dimensionless)
      'SSQ_ID': '',    // Survey sequence ID (dimensionless)
      'TSQ_ID': '',    // Tool sequence ID (dimensionless)
      'ROTW': 'RPM',   // Rotational weight/drilling rate
      'PROPEFF': '%',  // Propulsion efficiency
      'TFDS': '',      // Toolface status (dimensionless)
      
      // Gamma radiation measurements
      'Corrected Gamma': 'CPS',
      
      // Electrical measurements
      'BATV': 'V',
      'BAT2': 'V',
      
      // Flow state (dimensionless percentage or state)
      'FLOW': '%',
      'Flow': '%',
      
      // Vibration and shock levels (dimensionless)
      'PACKED_SHKVIBE_LVL': '',
      'Packed Shock & Vibration level': '',
      'Stick Slip Index Level': '',
      
      // Sequence and synchronization (dimensionless counters)
      'SYNC': '',
      'Sync': '',
      'Telemetry Survey Sequence ID': '',
      'Telemetry Toolface / Logging Sequence ID': '',
    };
    
    // Handle unknown variables gracefully with intelligent unit inference
    if (!(variableName in unitMap)) {
      const inferredUnit = TelemetryParser.inferUnitFromVariableName(variableName);
      console.info(`🔍 Unknown telemetry variable: "${variableName}". Inferred unit: "${inferredUnit}"`);
      return inferredUnit;
    }
    
    return unitMap[variableName];
  }

  /**
   * Intelligently infer units for unknown telemetry variables based on naming patterns
   * This allows the parser to handle user-configurable and ever-expanding telemetry variables
   */
  private static inferUnitFromVariableName(variableName: string): string {
    const name = variableName.toUpperCase();
    
    // Accelerometer patterns (calibrated or raw)
    if (name.includes('ACCEL') || name.includes('_AX') || name.includes('_AY') || name.includes('_AZ') || 
        name.match(/^A[XYZ]/) || name.includes('CALIBRATED_A')) {
      return 'g';
    }
    
    // Magnetometer patterns (calibrated or raw)
    if (name.includes('MAG') || name.includes('_MX') || name.includes('_MY') || name.includes('_MZ') || 
        name.match(/^M[XYZ]/) || name.includes('CALIBRATED_M')) {
      return 'G';
    }
    
    // Gyroscope patterns (calibrated or raw)
    if (name.includes('GYRO') || name.includes('_GX') || name.includes('_GY') || name.includes('_GZ') || 
        name.match(/^G[XYZ]/) || name.includes('CALIBRATED_G')) {
      return 'dps';
    }
    
    // Temperature patterns
    if (name.includes('TEMP') || name.includes('TEMPERATURE')) {
      return '°C';
    }
    
    // Pressure patterns  
    if (name.includes('PRESS') || name.includes('PRESSURE')) {
      return 'psi';
    }
    
    // Voltage/battery patterns
    if (name.includes('VOLT') || name.includes('BAT') || name.includes('BATTERY')) {
      return 'V';
    }
    
    // RPM patterns
    if (name.includes('RPM') || name.includes('ROTATION')) {
      return 'RPM';
    }
    
    // Angle patterns (inclination, azimuth, toolface)
    if (name.includes('INCL') || name.includes('AZIM') || name.includes('ANGLE') || 
        name.includes('TOOLFACE') || name.includes('DEGREES')) {
      return 'degrees';
    }
    
    // Gamma/radiation patterns
    if (name.includes('GAMMA') || name.includes('CPS') || name.includes('RADIATION')) {
      return 'cps';
    }
    
    // Flow patterns  
    if (name.includes('FLOW')) {
      return '%';
    }
    
    // Vibration/shock patterns
    if (name.includes('VIB') || name.includes('SHOCK')) {
      return 'g';
    }
    
    // Efficiency/percentage patterns
    if (name.includes('EFF') || name.includes('PERCENT')) {
      return '%';
    }
    
    // Status, ID, sequence patterns (dimensionless)
    if (name.includes('STAT') || name.includes('_ID') || name.includes('SEQ') || 
        name.includes('SYNC') || name.includes('COUNT')) {
      return '';
    }
    
    // Default fallback for truly unknown patterns
    return '';
  }
}